import { Picture } from "@/components/media/Picture";
import { Link, createFileRoute } from "@tanstack/react-router";

import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { PageHero } from "@/components/layout/PageHero";
import { heroFor } from "@/data/heroes";
import { site } from "@/data/site";
import { pageBlocks } from "@/lib/content";

const TITLE = "Donate — Light of Life International";
const DESCRIPTION =
  "Partner with Light of Life International. Your tax deductible gift funds gospel crusades and compassion outreach in Latin America and Africa.";

export const Route = createFileRoute("/donate")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://loli.ardent.dev/donate" },
    ],
    links: [{ rel: "canonical", href: "https://loli.ardent.dev/donate" }],
  }),
  component: DonatePage,
});

function DonatePage() {
  const blocks = pageBlocks("donate");
  const text = (i: number) => {
    const block = blocks[i];
    return block && "text" in block ? block.text : "";
  };

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <Header overlay />

      <main id="main" className="flex-1">
        <PageHero
          title={text(0) || "Donate"}
          image={heroFor("/donate").image}
          subtitle={text(1)}
          attribution={text(2)}
        />

        {/* Intro: photo left, copy right */}
        <section className="section-y">
          <div className="mx-auto grid max-w-[110rem] items-center gap-12 lg:grid-cols-2">
            <Picture
              src="/media/p7ye5zbudfiljzhzfnzd.webp"
              alt="Children in bright vests cheering during a Light of Life International outreach"
              className="h-[26rem] w-full rounded-r-2xl object-cover"
              sizes="(max-width: 768px) 100vw, 50vw"
              widths={[640, 1024]}
            />
            <div className="container-site max-w-xl lg:px-0">
              <h2 className="text-4xl text-navy sm:text-5xl">{text(3) || "Donate"}</h2>
              <p className="mt-6 text-foreground">{text(4)}</p>
            </div>
          </div>
        </section>

        {/* Three ways to give */}
        <section className="container-site pb-16">
          <div className="rounded-3xl bg-navy px-6 py-20 sm:px-12">
            <h2 className="mx-auto max-w-4xl text-center text-4xl text-white sm:text-5xl">
              {text(5)}
            </h2>
            <div className="mt-14 grid gap-6 md:grid-cols-3">
              <div className="rounded-xl bg-white px-8 py-10 text-center">
                <h3 className="text-2xl text-foreground">{text(6)}</h3>
                <address className="mt-6 not-italic text-foreground">
                  {site.name}
                  <br />
                  4364 Western Center Blvd #124
                  <br />
                  Fort Worth, Texas 76137
                </address>
              </div>
              <div className="rounded-xl bg-white px-8 py-10 text-center">
                <h3 className="text-2xl text-foreground">{text(8)}</h3>
                <Link
                  to="/donation-form"
                  className="mt-8 inline-block rounded-lg bg-[#333333] px-8 py-3 font-sans font-semibold text-white transition hover:bg-black"
                >
                  Donate
                </Link>
              </div>
              <div className="rounded-xl bg-white px-8 py-10 text-center">
                <h3 className="text-2xl text-foreground">{text(9)}</h3>
                <p className="mt-6 text-foreground">
                  Scan in your banking app to pay LIGHT OF LIFE INTERNATIONAL, INC at
                  gifts@loli.org.
                </p>
                <img
                  src="/media/zelle.png"
                  alt="Zelle QR code for giving to Light of Life International"
                  loading="lazy"
                  decoding="async"
                  className="mx-auto mt-6 w-40"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Join Us CTA */}
        <section className="container-site pb-24">
          <div className="rounded-3xl bg-[#2f2f2f] px-6 py-20 text-center sm:px-12">
            <h2 className="text-4xl text-white sm:text-5xl">{text(11) || "Join Us"}</h2>
            <p className="mx-auto mt-6 max-w-3xl text-white/90">{text(12)}</p>
            <Link
              to="/contact"
              className="mt-10 inline-block rounded-lg bg-accent px-8 py-3 font-sans font-semibold text-navy transition hover:brightness-110"
            >
              Contact us
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
